import { ref, computed, onMounted, onUnmounted } from 'vue'
import { mapService } from '../services/mapService'

// Tipos de datos del mapa
export interface Incident {
  id: string
  title: string
  description: string
  priority: 'BAJA' | 'MEDIA' | 'ALTA' | 'CRITICA'
  status: 'ABIERTA' | 'EN_PROGRESO' | 'RESUELTA' | 'CERRADA'
  latitude: number
  longitude: number
  createdAt: string
  updatedAt: string
  assignedSupervisor?: string
  category: string
  affectedAgencies?: string[]
}

export interface Supervisor {
  id: string
  name: string
  email: string
  phone: string
  status: 'ACTIVO' | 'INACTIVO' | 'EN_RUTA' | 'DISPONIBLE'
  currentLatitude?: number
  currentLongitude?: number
  assignedZone: string
  activeIncidents: string[]
  lastUpdate: string
}

export interface Agency {
  id: string
  name: string
  code: string
  address: string
  latitude: number
  longitude: number
  status: 'ACTIVA' | 'INACTIVA' | 'MANTENIMIENTO'
  zone: string
  contactInfo: {
    phone?: string
    email?: string
    manager?: string
  }
  incidentCount: number
  lastIncident?: string
}

export interface Route {
  id: string
  name: string
  description: string
  status: 'ACTIVA' | 'PAUSADA' | 'COMPLETADA'
  supervisorId: string
  coordinates: Array<[number, number]>
  checkpoints: Array<{
    id: string
    name: string
    latitude: number
    longitude: number
    status: 'PENDIENTE' | 'VISITADO' | 'OMITIDO'
    visitedAt?: string
  }>
  estimatedDuration: number
  actualDuration?: number
  createdAt: string
}

// Filtros de datos
export interface MapFilters {
  incidents: {
    priorities: string[]
    statuses: string[]
    categories: string[]
    dateRange: {
      start?: string
      end?: string
    }
  }
  supervisors: {
    statuses: string[]
    zones: string[]
  }
  agencies: {
    statuses: string[]
    zones: string[]
  }
  routes: {
    statuses: string[]
    supervisors: string[]
  }
}

// Estadísticas del mapa
export interface MapStatistics {
  totalIncidents: number
  criticalIncidents: number
  activeSupervisors: number
  totalSupervisors: number
  totalAgencies: number
  activeAgencies: number
  activeRoutes: number
  completedRoutes: number
}

export function useMapData() {
  // Estados reactivos principales
  const incidents = ref<Incident[]>([])
  const supervisors = ref<Supervisor[]>([])
  const agencies = ref<Agency[]>([])
  const routes = ref<Route[]>([])
  
  // Estados de carga
  const loading = ref(false)
  const incidentsLoading = ref(false)
  const supervisorsLoading = ref(false)
  const agenciesLoading = ref(false)
  const routesLoading = ref(false)
  
  // Estados de error
  const error = ref<string | null>(null)
  const incidentsError = ref<string | null>(null)
  const supervisorsError = ref<string | null>(null)
  const agenciesError = ref<string | null>(null)
  const routesError = ref<string | null>(null)
  
  // Filtros activos
  const filters = ref<MapFilters>({
    incidents: {
      priorities: ['ALTA', 'CRITICA'],
      statuses: ['ABIERTA', 'EN_PROGRESO'],
      categories: [],
      dateRange: {}
    },
    supervisors: {
      statuses: ['ACTIVO', 'EN_RUTA'],
      zones: []
    },
    agencies: {
      statuses: ['ACTIVA'],
      zones: []
    },
    routes: {
      statuses: ['ACTIVA'],
      supervisors: []
    }
  })
  
  // Configuración de actualización automática
  const autoRefresh = ref(true)
  const refreshInterval = ref(30000) // 30 segundos
  let refreshTimer: NodeJS.Timeout | null = null
  
  // Datos filtrados computados
  const filteredIncidents = computed(() => {
    return incidents.value.filter(incident => {
      const priorityMatch = filters.value.incidents.priorities.length === 0 || 
        filters.value.incidents.priorities.includes(incident.priority)
      
      const statusMatch = filters.value.incidents.statuses.length === 0 || 
        filters.value.incidents.statuses.includes(incident.status)
      
      const categoryMatch = filters.value.incidents.categories.length === 0 || 
        filters.value.incidents.categories.includes(incident.category)
      
      let dateMatch = true
      if (filters.value.incidents.dateRange.start) {
        dateMatch = dateMatch && new Date(incident.createdAt) >= new Date(filters.value.incidents.dateRange.start)
      }
      if (filters.value.incidents.dateRange.end) {
        dateMatch = dateMatch && new Date(incident.createdAt) <= new Date(filters.value.incidents.dateRange.end)
      }
      
      return priorityMatch && statusMatch && categoryMatch && dateMatch
    })
  })
  
  const filteredSupervisors = computed(() => {
    return supervisors.value.filter(supervisor => {
      const statusMatch = filters.value.supervisors.statuses.length === 0 || 
        filters.value.supervisors.statuses.includes(supervisor.status)
      
      const zoneMatch = filters.value.supervisors.zones.length === 0 || 
        filters.value.supervisors.zones.includes(supervisor.assignedZone)
      
      return statusMatch && zoneMatch
    })
  })
  
  const filteredAgencies = computed(() => {
    return agencies.value.filter(agency => {
      const statusMatch = filters.value.agencies.statuses.length === 0 || 
        filters.value.agencies.statuses.includes(agency.status)
      
      const zoneMatch = filters.value.agencies.zones.length === 0 || 
        filters.value.agencies.zones.includes(agency.zone)
      
      return statusMatch && zoneMatch
    })
  })
  
  const filteredRoutes = computed(() => {
    const list = Array.isArray(routes.value) ? routes.value : []
    return list.filter(route => {
      const statusMatch = filters.value.routes.statuses.length === 0 || 
        filters.value.routes.statuses.includes(route.status)
      
      const supervisorMatch = filters.value.routes.supervisors.length === 0 || 
        filters.value.routes.supervisors.includes(route.supervisorId)
      
      return statusMatch && supervisorMatch
    })
  })
  
  // Estadísticas computadas
  const statistics = computed<MapStatistics>(() => {
    return {
      totalIncidents: incidents.value.length,
      criticalIncidents: incidents.value.filter(i => i.priority === 'CRITICA').length,
      activeSupervisors: supervisors.value.filter(s => s.status === 'ACTIVO' || s.status === 'EN_RUTA').length,
      totalSupervisors: supervisors.value.length,
      totalAgencies: agencies.value.length,
      activeAgencies: agencies.value.filter(a => a.status === 'ACTIVA').length,
      activeRoutes: routes.value.filter(r => r.status === 'ACTIVA').length,
      completedRoutes: routes.value.filter(r => r.status === 'COMPLETADA').length
    }
  })
  
  // Datos únicos para filtros
  const availableCategories = computed(() => {
    return [...new Set(incidents.value.map(i => i.category))].sort()
  })
  
  const availableZones = computed(() => {
    const supervisorZones = [...new Set(supervisors.value.map(s => s.assignedZone))]
    const agencyZones = [...new Set(agencies.value.map(a => a.zone))]
    return [...new Set([...supervisorZones, ...agencyZones])].sort()
  })
  
  const availableSupervisors = computed(() => {
    return supervisors.value.map(s => ({ id: s.id, name: s.name }))
  })
  
  // Métodos de carga de datos
  const loadIncidents = async (force = false) => {
    if (!force && incidents.value.length > 0) return
    
    incidentsLoading.value = true
    incidentsError.value = null
    
    try {
      const data = await mapService.getIncidents()
      incidents.value = data
    } catch (err) {
      incidentsError.value = err instanceof Error ? err.message : 'Error loading incidents'
      console.error('Error loading incidents:', err)
    } finally {
      incidentsLoading.value = false
    }
  }
  
  const loadSupervisors = async (force = false) => {
    if (!force && supervisors.value.length > 0) return
    
    supervisorsLoading.value = true
    supervisorsError.value = null
    
    try {
      const data = await mapService.getSupervisors()
      supervisors.value = data
    } catch (err) {
      supervisorsError.value = err instanceof Error ? err.message : 'Error loading supervisors'
      console.error('Error loading supervisors:', err)
    } finally {
      supervisorsLoading.value = false
    }
  }
  
  const loadAgencies = async (force = false) => {
    if (!force && agencies.value.length > 0) return
    
    agenciesLoading.value = true
    agenciesError.value = null
    
    try {
      const data = await mapService.getAgencies()
      agencies.value = data
    } catch (err) {
      agenciesError.value = err instanceof Error ? err.message : 'Error loading agencies'
      console.error('Error loading agencies:', err)
    } finally {
      agenciesLoading.value = false
    }
  }
  
  const loadRoutes = async (force = false) => {
    if (!force && routes.value.length > 0) return
    
    routesLoading.value = true
    routesError.value = null
    
    try {
      const data = await mapService.getRoutes()
      routes.value = data
    } catch (err) {
      routesError.value = err instanceof Error ? err.message : 'Error loading routes'
      console.error('Error loading routes:', err)
    } finally {
      routesLoading.value = false
    }
  }
  
  // Método principal de carga
  const loadAllData = async (force = false) => {
    loading.value = true
    error.value = null
    
    try {
      await Promise.all([
        loadIncidents(force),
        loadSupervisors(force),
        loadAgencies(force),
        loadRoutes(force)
      ])
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Error loading map data'
      console.error('Error loading all data:', err)
    } finally {
      loading.value = false
    }
  }
  
  // Métodos de filtrado
  const updateFilters = (newFilters: Partial<MapFilters>) => {
    filters.value = { ...filters.value, ...newFilters }
  }
  
  const resetFilters = () => {
    filters.value = {
      incidents: {
        priorities: ['ALTA', 'CRITICA'],
        statuses: ['ABIERTA', 'EN_PROGRESO'],
        categories: [],
        dateRange: {}
      },
      supervisors: {
        statuses: ['ACTIVO', 'EN_RUTA'],
        zones: []
      },
      agencies: {
        statuses: ['ACTIVA'],
        zones: []
      },
      routes: {
        statuses: ['ACTIVA'],
        supervisors: []
      }
    }
  }
  
  // Métodos de actualización automática
  const startAutoRefresh = () => {
    if (refreshTimer) clearInterval(refreshTimer)
    
    refreshTimer = setInterval(() => {
      if (autoRefresh.value) {
        loadAllData(true)
      }
    }, refreshInterval.value)
  }
  
  const stopAutoRefresh = () => {
    if (refreshTimer) {
      clearInterval(refreshTimer)
      refreshTimer = null
    }
  }
  
  const toggleAutoRefresh = () => {
    autoRefresh.value = !autoRefresh.value
    if (autoRefresh.value) {
      startAutoRefresh()
    } else {
      stopAutoRefresh()
    }
  }
  
  // Método para refrescar datos manualmente
  const refreshData = async () => {
    await loadAllData(true)
  }
  
  // Métodos de búsqueda y selección
  const searchIncidents = (query: string) => {
    if (!query.trim()) return filteredIncidents.value
    
    const searchTerm = query.toLowerCase()
    return filteredIncidents.value.filter(incident =>
      incident.title.toLowerCase().includes(searchTerm) ||
      incident.description.toLowerCase().includes(searchTerm) ||
      incident.category.toLowerCase().includes(searchTerm)
    )
  }
  
  const getIncidentById = (id: string) => {
    return incidents.value.find(incident => incident.id === id)
  }
  
  const getSupervisorById = (id: string) => {
    return supervisors.value.find(supervisor => supervisor.id === id)
  }
  
  const getAgencyById = (id: string) => {
    return agencies.value.find(agency => agency.id === id)
  }
  
  const getRouteById = (id: string) => {
    return routes.value.find(route => route.id === id)
  }
  
  // Lifecycle hooks
  onMounted(() => {
    loadAllData()
    if (autoRefresh.value) {
      startAutoRefresh()
    }
  })
  
  onUnmounted(() => {
    stopAutoRefresh()
  })
  
  return {
    // Datos principales
    incidents,
    supervisors,
    agencies,
    routes,
    
    // Datos filtrados
    filteredIncidents,
    filteredSupervisors,
    filteredAgencies,
    filteredRoutes,
    
    // Estados de carga
    loading,
    incidentsLoading,
    supervisorsLoading,
    agenciesLoading,
    routesLoading,
    
    // Estados de error
    error,
    incidentsError,
    supervisorsError,
    agenciesError,
    routesError,
    
    // Filtros y estadísticas
    filters,
    statistics,
    
    // Datos únicos para filtros
    availableCategories,
    availableZones,
    availableSupervisors,
    
    // Configuración de auto-refresh
    autoRefresh,
    refreshInterval,
    
    // Métodos de carga
    loadAllData,
    loadIncidents,
    loadSupervisors,
    loadAgencies,
    loadRoutes,
    
    // Métodos de filtrado
    updateFilters,
    resetFilters,
    
    // Métodos de actualización
    startAutoRefresh,
    stopAutoRefresh,
    toggleAutoRefresh,
    refreshData,
    
    // Métodos de búsqueda
    searchIncidents,
    getIncidentById,
    getSupervisorById,
    getAgencyById,
    getRouteById
  }
}
